import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

type SectionTrackerContextType = {
  rightContentContainerRef: React.RefObject<HTMLDivElement>
  activePosition: number
  handleScrollToSection: (position: number) => void
  reportSectionVisibility: (props: {
    position: number
    isVisible: boolean
  }) => void
  registerSection: (position: number, element: HTMLDivElement) => void
  unregisterSection: (position: number) => void
}

const SectionTrackerContext = createContext<SectionTrackerContextType>({
  rightContentContainerRef: null,
  activePosition: 0,
  handleScrollToSection: () => {},
  reportSectionVisibility: () => {},
  registerSection: () => {},
  unregisterSection: () => {}
})

const SCROLL_LOCK_TIMEOUT_MS = 1000

export const SectionTrackerProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  // State to track the index of the currently active section.
  const [activePosition, setActivePosition] = useState<number>(0)
  // Ref to store the set of section indices currently visible in the viewport.
  const visibleSectionsRef = useRef(new Set<number>())
  // Ref for the DOM element of the scrollable container.
  const rightContentContainerRef = useRef<HTMLDivElement>(null)
  // Ref to map section indices to their corresponding DOM elements for easy access.
  const sectionElementsRef = useRef<Map<number, HTMLDivElement>>(new Map())
  // This ref is used as a lock to prevent visibility updates from overriding the active
  // section during a programmatic scroll (e.g., when a user clicks a category).
  const allowVisibilityUpdate = useRef(true)
  // Ref to hold the timeout ID for the scroll lock, allowing it to be cleared.
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Caches a section's DOM element by its position index.
  const registerSection = useCallback(
    (position: number, element: HTMLDivElement) => {
      sectionElementsRef.current.set(position, element)
    },
    []
  )

  // Removes a section's DOM element from the cache when it unmounts.
  const unregisterSection = useCallback((position: number) => {
    sectionElementsRef.current.delete(position)
  }, [])

  // Handles programmatic scrolling to a specific section.
  const handleScrollToSection = useCallback((position: number) => {
    // Set the active position immediately for a responsive user experience.
    setActivePosition(position)
    const sectionElement = sectionElementsRef.current.get(position)
    if (rightContentContainerRef.current && sectionElement) {
      // Disable visibility updates to prevent the active section from changing during the scroll animation.
      allowVisibilityUpdate.current = false
      rightContentContainerRef.current.scrollTo({
        top: sectionElement.offsetTop,
        behavior: 'smooth'
      })
      // Clear any existing timeout to handle rapid clicks.
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      // Set a timeout to re-enable visibility updates after the scroll animation is expected to complete.
      scrollTimeoutRef.current = setTimeout(() => {
        allowVisibilityUpdate.current = true
      }, SCROLL_LOCK_TIMEOUT_MS)
    }
  }, [])

  // Callback for section components to report their visibility status.
  const reportSectionVisibility = useCallback(
    ({position, isVisible}: {position: number; isVisible: boolean}) => {
      // Update the set of visible sections.
      if (isVisible) {
        visibleSectionsRef.current.add(position)
      } else {
        visibleSectionsRef.current.delete(position)
      }

      // If there are visible sections and updates are not locked, determine the top-most visible section.
      if (
        visibleSectionsRef.current.size > 0 &&
        allowVisibilityUpdate.current
      ) {
        const topSectionPosition = Math.min(
          ...Array.from(visibleSectionsRef.current)
        )
        // Update the active position only if it has changed.
        setActivePosition(currentActivePosition =>
          currentActivePosition !== topSectionPosition
            ? topSectionPosition
            : currentActivePosition
        )
      }
    },
    []
  )

  // Effect to handle manual user scroll interruptions.
  useEffect(() => {
    const container = rightContentContainerRef.current
    // This handler gives control back to the user's manual scroll.
    const onUserInteraction = () => {
      // Re-enable visibility updates immediately on user interaction.
      allowVisibilityUpdate.current = true
      // Clear the scroll lock timeout, as the programmatic scroll has been interrupted.
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
    if (container) {
      container.addEventListener('wheel', onUserInteraction)
      container.addEventListener('touchstart', onUserInteraction)
      return () => {
        container.removeEventListener('wheel', onUserInteraction)
        container.removeEventListener('touchstart', onUserInteraction)
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current)
        }
      }
    }
  }, [])

  return (
    <SectionTrackerContext.Provider
      value={{
        activePosition,
        rightContentContainerRef,
        handleScrollToSection,
        reportSectionVisibility,
        registerSection,
        unregisterSection
      }}
    >
      {children}
    </SectionTrackerContext.Provider>
  )
}

export const useSectionTracker = () => useContext(SectionTrackerContext)
